import { useEffect } from 'react';

import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from 'redux/store/store.types';
import { getApiErrorMessage } from 'utils';

import { useAuthSession } from 'modules/auth-session';
import { useUsers } from 'modules/users';

import {
  useSaveExchangeSettingsMutation,
  useUpdateExchangeOrdersVisibilityMutation,
} from '../api/exchange-settings.api';
import { ExchangeSettingsAC, setCompanyUsers } from '../store/exchange-settings.slice';
import { ExchangeOrdersVisibilityPayload, ExchangeSettingPayload } from '../types/exchange-settings.type';

interface UseExchangeSettings {
  saveExchangeSettingsHandler: (payload: ExchangeSettingPayload) => Promise<void>;
  saveExchangeOrdersVisibilityHandler: (payload: ExchangeOrdersVisibilityPayload) => Promise<boolean>;
}

export const useExchangeSettings = (): UseExchangeSettings => {
  const dispatch = useAppDispatch();

  const { currentUser } = useAppSelector((state) => state.authSession);

  const { checkSessionHandler } = useAuthSession();
  const { fetchUsersSimpleHandler } = useUsers();

  const [updateExchangeSettingsQuery] = useSaveExchangeSettingsMutation();
  const [updateExchangeOrdersVisibilityQuery] = useUpdateExchangeOrdersVisibilityMutation();

  const saveExchangeSettingsHandler = async (payload: ExchangeSettingPayload) => {
    dispatch(ExchangeSettingsAC.toggleLoading(true));
    const { error } = await updateExchangeSettingsQuery(payload);

    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to save Exchange Settings!');
      dispatch(ExchangeSettingsAC.toggleLoading(false));
      return;
    }
    await checkSessionHandler();
    toast.success('Exchange Settings successfully saved!');
    dispatch(ExchangeSettingsAC.toggleLoading(false));
  };

  const saveExchangeOrdersVisibilityHandler = async (payload: ExchangeOrdersVisibilityPayload) => {
    dispatch(ExchangeSettingsAC.toggleLoading(true));
    const { error } = await updateExchangeOrdersVisibilityQuery(payload);

    if (error) {
      toast.error(getApiErrorMessage(error) || 'Failed to save settings!');
      dispatch(ExchangeSettingsAC.toggleLoading(false));
      return false;
    }
    await checkSessionHandler();
    toast.success('Settings successfully saved!');
    dispatch(ExchangeSettingsAC.toggleLoading(false));
    return true;
  };

  const getCompanyUsers = async () => {
    const result = await fetchUsersSimpleHandler({
      page: 1,
      per_page: 100,
      filter_by_company_id: currentUser?.company?.id,
    });
    dispatch(setCompanyUsers(result || []));
  };

  useEffect(() => {
    getCompanyUsers();
  }, []);

  return {
    saveExchangeSettingsHandler,
    saveExchangeOrdersVisibilityHandler,
  };
};
